import { useCallback, useEffect, useRef } from 'react';

const HISTORY_LIMIT = 50;

/**
 * Minimal undo/redo stack driven by JSON snapshots of the canvas elements array.
 * Coalesces rapid changes into a single history entry (250 ms quiet window).
 */
export const useHistory = (
  elements: unknown[],
  setElements: (next: unknown[]) => void,
) => {
  const stack = useRef<unknown[][]>([]);
  const index = useRef(-1);
  const lastSnapshotAt = useRef(0);
  const elementsRef = useRef(elements);
  const setElementsRef = useRef(setElements);

  useEffect(() => {
    elementsRef.current = elements;
    setElementsRef.current = setElements;
  });

  useEffect(() => {
    // Skip the very first snapshot (initial load) by checking if we already have one.
    const now = Date.now();
    if (now - lastSnapshotAt.current < 250) return;
    lastSnapshotAt.current = now;
    const snap = JSON.parse(JSON.stringify(elements));
    stack.current = stack.current.slice(0, index.current + 1);
    stack.current.push(snap);
    if (stack.current.length > HISTORY_LIMIT) stack.current.shift();
    index.current = stack.current.length - 1;
  }, [elements]);

  const undo = useCallback(() => {
    if (index.current <= 0) return;
    index.current -= 1;
    const snap = stack.current[index.current];
    if (snap) setElementsRef.current(JSON.parse(JSON.stringify(snap)));
  }, []);

  const redo = useCallback(() => {
    if (index.current >= stack.current.length - 1) return;
    index.current += 1;
    const snap = stack.current[index.current];
    if (snap) setElementsRef.current(JSON.parse(JSON.stringify(snap)));
  }, []);

  // Cmd/Ctrl+Z / Shift+Z
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.tagName === 'INPUT') return;
      if ((e.target as HTMLElement | null)?.tagName === 'TEXTAREA') return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
        undo();
        e.preventDefault();
      } else if ((e.key.toLowerCase() === 'z' && e.shiftKey) || e.key.toLowerCase() === 'y') {
        redo();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return { undo, redo };
};
